import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useToast } from '@/shared/hooks/useToast';
import { ganttService } from '../api/gantt.service';
import { TASK_KEYS } from './queryKeys';

interface AddDependencyVars {
  /** Dependent task (the endpoint's `{taskId}`). */
  successorTaskId: string;
  predecessorTaskId: string;
  lagDays?: number;
}

interface RemoveDependencyVars {
  successorTaskId: string;
  dependencyId: string;
}

/** Create a finish-to-start dependency by drawing a connector on the chart. */
export function useAddTaskDependency(projectId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;
  const { t } = useTranslation('tasks');
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ successorTaskId, predecessorTaskId, lagDays = 0 }: AddDependencyVars) =>
      ganttService.addDependency(projectId, successorTaskId, { predecessorTaskId, lagDays }),

    onSuccess: () => {
      toast.success({ title: t('gantt.dependency.createSuccess') });
      queryClient.invalidateQueries({
        queryKey: TASK_KEYS.ganttDependencies(orgId, projectId),
      });
    },
  });
}

export function useRemoveTaskDependency(projectId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;
  const { t } = useTranslation('tasks');
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ successorTaskId, dependencyId }: RemoveDependencyVars) =>
      ganttService.removeDependency(projectId, successorTaskId, dependencyId),

    onSuccess: () => {
      toast.success({ title: t('gantt.dependency.deleteSuccess') });
      queryClient.invalidateQueries({
        queryKey: TASK_KEYS.ganttDependencies(orgId, projectId),
      });
    },
  });
}
